import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { ArrowRight, Settings, Mail } from 'lucide-react';

function openConsent() {
  window.dispatchEvent(new CustomEvent('kitxens:open-cookie-prefs'));
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 tracking-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-neutral-700">{children}</div>
    </section>
  );
}

export default function CookiePolicyPage() {
  const { lang } = useT();
  return (
    <div className="pt-28 pb-20">
      <SEO title={lang === 'es' ? 'Política de Cookies' : 'Cookie Policy'} description="How Kitxens uses cookies and how you can manage your preferences." path="/cookies" />
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-[#F77F11] text-xs tracking-[0.25em] font-semibold mb-3">LEGAL</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 tracking-[-0.02em]">Cookie Policy</h1>
          <p className="mt-3 text-sm text-neutral-500">Last updated: 2026</p>
        </motion.div>

        <Section title="What are cookies">
          <p>Cookies are small text files placed on your device by websites you visit. They help sites work properly, remember your preferences, measure performance and personalize content.</p>
        </Section>

        <Section title="How we use cookies">
          <p>Kitxens uses cookies and similar technologies for the following purposes:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Necessary cookies</strong> — required for the website to function (security, navigation, form submissions). These cannot be disabled.</li>
            <li><strong>Functional cookies</strong> — remember preferences like language (EN/ES) and theme.</li>
            <li><strong>Analytics cookies</strong> — help us understand how visitors use the site so we can improve it.</li>
            <li><strong>Marketing cookies</strong> — used to deliver and measure the relevance of campaigns and content.</li>
          </ul>
        </Section>

        <Section title="Managing your preferences">
          <p>You can update your consent at any time. Choices are remembered on your device and can be revoked or changed below.</p>
          <button
            onClick={openConsent}
            className="inline-flex items-center gap-2 mt-2 bg-neutral-900 hover:bg-neutral-800 text-white px-5 h-11 rounded-full text-sm font-semibold"
          >
            <Settings className="w-4 h-4" /> Manage cookie preferences
          </button>
        </Section>

        <Section title="Revoking consent">
          <p>To revoke previously granted consent, open the preferences panel above and toggle off any category, or clear your browser cookies for kitxens.com.</p>
        </Section>

        <Section title="Contact">
          <p>Questions about how we use cookies? Email <a href="mailto:info@kitxens.com" className="text-[#F77F11] underline">info@kitxens.com</a>.</p>
        </Section>

        <div className="mt-12 p-6 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-neutral-900">Want to learn more?</p>
            <p className="text-sm text-neutral-600">Read our Privacy Policy and Terms.</p>
          </div>
          <Link to="/privacy" className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white px-5 h-11 rounded-full text-sm font-semibold whitespace-nowrap">
            <Mail className="w-4 h-4" /> Privacy Policy <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
